import { useCallback, useEffect, useState } from "react";
import { Box, Button, CloseButton, Dialog, Field, HStack, Input, Portal, Spinner, Text, VStack } from "@chakra-ui/react";
import { QRCodeSVG } from "qrcode.react";
import AccountService from "@/services/AccountService";
import { showToast } from "@/components/ui/toaster";
import PasswordInput from "@/components/ui/foundation/PasswordInput";
import useResendCooldown from "@/auth/useResendCooldown";

const DialogShell = ({ open, title, onClose, children }) => (
    <Dialog.Root lazyMount open={open} onOpenChange={(e) => !e.open && onClose()} placement="center">
        <Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner>
                <Dialog.Content bg="bg.card" borderWidth={1} borderColor="border.secondary" borderRadius={12} p={2} minW={96} maxW="md">
                    <Dialog.Header>
                        <Dialog.Title color="white">{title}</Dialog.Title>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton color="white" size="sm" />
                        </Dialog.CloseTrigger>
                    </Dialog.Header>
                    <Dialog.Body pb={6}>{children}</Dialog.Body>
                </Dialog.Content>
            </Dialog.Positioner>
        </Portal>
    </Dialog.Root>
);

const CodeInput = ({ label, value, onChange }) => (
    <Field.Root required>
        <Field.Label color="white">{label} <Field.RequiredIndicator /></Field.Label>
        <Input
            variant="primary"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={value}
            onChange={(e) => onChange(e.target.value)}
        />
    </Field.Root>
);

export const SetupTotpDialog = ({ open, onClose, onEnabled }) => {
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [isConfirming, setIsConfirming] = useState(false);

    useEffect(() => {
        if (!open) return;
        setSetup(null);
        setCode("");
        setError("");
        AccountService.setupTwoFactor()
            .then(setSetup)
            .catch(() => setError("Couldn't generate the 2FA setup. Please try again."));
    }, [open]);

    const handleConfirm = async () => {
        setError("");
        setIsConfirming(true);
        try {
            await AccountService.confirmTwoFactor(code);
            showToast("Two-factor authentication enabled", null, "success");
            onEnabled();
            onClose();
        } catch (err) {
            setError(err?.response?.status === 400
                ? "The code is invalid or has expired. Check the current code in your Authenticator app."
                : "Something went wrong. Please try again.");
        } finally {
            setIsConfirming(false);
        }
    };

    return (
        <DialogShell open={open} title="Set up an Authenticator app" onClose={onClose}>
            {!setup && !error ? (
                <VStack py={8}><Spinner color="white" size="lg" /></VStack>
            ) : (
                <VStack gap={4} align="stretch">
                    <Text color="text.grey" fontSize="sm">
                        Scan the QR code with Google Authenticator or Microsoft Authenticator (or enter the key manually), then type the generated code to confirm.
                    </Text>
                    {setup?.otpAuthUri && (
                        <Box bg="white" p={3} borderRadius={8} alignSelf="center">
                            <QRCodeSVG value={setup.otpAuthUri} size={180} />
                        </Box>
                    )}
                    {setup?.secret && (
                        <Box>
                            <Text color="text.grey" fontSize="xs" mb={1}>Manual entry key</Text>
                            <Text color="white" fontSize="sm" fontFamily="mono" wordBreak="break-all">{setup.secret}</Text>
                        </Box>
                    )}
                    <CodeInput label="Code from the app" value={code} onChange={setCode} />
                    {error && <Text color="red.400" fontSize="sm">{error}</Text>}
                    <Button variant="primary" onClick={handleConfirm} loading={isConfirming} disabled={!code || !setup}>
                        Confirm
                    </Button>
                </VStack>
            )}
        </DialogShell>
    );
};

export const SetupEmailDialog = ({ open, email, onClose, onEnabled }) => {
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const resendCooldown = useResendCooldown();
    const { start: startCooldown } = resendCooldown;

    const sendCode = useCallback(async (isResend) => {
        setError("");
        setInfo("");
        setIsSending(true);
        try {
            await AccountService.sendEmailTwoFactorSetupCode();
            if (isResend) setInfo("We sent a new code.");
            startCooldown();
        } catch (err) {
            // 429: a code was just sent and is still valid.
            if (err?.response?.status === 429) startCooldown();
            else setError("Couldn't send the code. Please try again in a moment.");
        } finally {
            setIsSending(false);
        }
    }, [startCooldown]);

    useEffect(() => {
        if (!open) return;
        setCode("");
        sendCode(false);
    }, [open, sendCode]);

    const handleConfirm = async () => {
        setError("");
        setInfo("");
        setIsConfirming(true);
        try {
            await AccountService.confirmEmailTwoFactor(code);
            showToast("Two-factor authentication enabled", null, "success");
            onEnabled();
            onClose();
        } catch (err) {
            setError(err?.response?.status === 400
                ? "The code is invalid or has expired. Send a new code and try again."
                : "Something went wrong. Please try again.");
        } finally {
            setIsConfirming(false);
        }
    };

    return (
        <DialogShell open={open} title="Enable 2FA via e-mail" onClose={onClose}>
            <VStack gap={4} align="stretch">
                <Text color="text.grey" fontSize="sm">
                    We sent a 6-digit code to <Text as="span" color="white">{email}</Text>. Enter it below to confirm the setup.
                </Text>
                <CodeInput label="Code from e-mail" value={code} onChange={setCode} />
                {error && <Text color="red.400" fontSize="sm">{error}</Text>}
                {info && !error && <Text color="green.400" fontSize="sm">{info}</Text>}
                <Button variant="primary" onClick={handleConfirm} loading={isConfirming} disabled={!code}>
                    Confirm
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => sendCode(true)}
                    loading={isSending}
                    disabled={resendCooldown.isCoolingDown}
                >
                    {resendCooldown.isCoolingDown ? `Resend in ${resendCooldown.secondsLeft}s` : "Resend code"}
                </Button>
            </VStack>
        </DialogShell>
    );
};

export const DisableTwoFactorDialog = ({ open, method: twoFactorMethod, onClose, onDisabled }) => {
    // E-mail 2FA is disabled with the password only.
    const allowCode = twoFactorMethod !== "Email";
    const [proof, setProof] = useState("password");
    const [value, setValue] = useState("");
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setProof("password");
        setValue("");
        setError("");
    }, [open]);

    const handleDisable = async () => {
        setError("");
        setIsSubmitting(true);
        try {
            await AccountService.disableTwoFactor(proof === "password" ? { password: value } : { code: value });
            showToast("Two-factor authentication disabled", null, "success");
            onDisabled();
            onClose();
        } catch (err) {
            setError(err?.response?.status === 400
                ? "The password or code is incorrect."
                : "Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const switchProof = (next) => {
        setProof(next);
        setValue("");
    };

    return (
        <DialogShell open={open} title="Disable two-factor authentication" onClose={onClose}>
            <VStack gap={4} align="stretch">
                <Text color="text.grey" fontSize="sm">
                    {allowCode
                        ? "Confirm it's you with your password or a current code from your Authenticator app."
                        : "Confirm it's you with your password."}
                </Text>
                {allowCode && (
                    <HStack gap={2}>
                        <Button size="sm" variant={proof === "password" ? "primary" : "outlined"} onClick={() => switchProof("password")}>
                            Password
                        </Button>
                        <Button size="sm" variant={proof === "code" ? "primary" : "outlined"} onClick={() => switchProof("code")}>
                            App code
                        </Button>
                    </HStack>
                )}
                {proof === "password" ? (
                    <Field.Root required>
                        <Field.Label color="white">Password <Field.RequiredIndicator /></Field.Label>
                        <PasswordInput value={value} onChange={(e) => setValue(e.target.value)} />
                    </Field.Root>
                ) : (
                    <CodeInput label="Code from the app" value={value} onChange={setValue} />
                )}
                {error && <Text color="red.400" fontSize="sm">{error}</Text>}
                <Button variant="primary" colorPalette="red" onClick={handleDisable} loading={isSubmitting} disabled={!value}>
                    Disable 2FA
                </Button>
            </VStack>
        </DialogShell>
    );
};
